import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { InfoIcon } from '@/components/InfoIcon';

// #region Sample data
const data = [
  { month: 'Sep 1', usage: 10500, costs: 5200 },
  { month: 'Sep 8', usage: 10800, costs: 5300 },
  { month: 'Sep 15', usage: 11000, costs: 5400 },
  { month: 'Sep 22', usage: 10700, costs: 5500 },
  { month: 'Oct 6', usage: 11200, costs: 5800 },
  { month: 'Oct 13', usage: 11500, costs: 6000 },
  { month: 'Oct 20', usage: 11800, costs: 6200 },
  { month: 'Oct 27', usage: 12000, costs: 6300 },
  { month: 'Nov 3', usage: 12200, costs: 6400 },
  { month: 'Nov 10', usage: 12500, costs: 6500 },
  { month: 'Nov 17', usage: 12800, costs: 6600 },
  { month: 'Nov 24', usage: 12434, costs: 6434 },
];

// Calculate stats
const calculateStats = () => {
  const lastWeek = data[data.length - 1];
  const previousWeek = data[data.length - 2];
  
  const totalUsage = data.reduce((sum, item) => sum + item.usage, 0);
  const avgCosts = data.reduce((sum, item) => sum + item.costs, 0) / data.length;
  
  const usageChange = ((lastWeek.usage - previousWeek.usage) / previousWeek.usage) * 100;
  const costsChange = ((lastWeek.costs - previousWeek.costs) / previousWeek.costs) * 100;
  
  return {
    totalUsage,
    avgCosts,
    usageChange,
    costsChange
  };
};

const stats = calculateStats();
// #endregion

// Custom tooltip component
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    // Find the usage and costs data
    const usageData = payload.find((p: any) => p.dataKey === 'usage');
    const costsData = payload.find((p: any) => p.dataKey === 'costs');

    return (
      <div
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          color: 'white',
          padding: '12px 16px',
          borderRadius: '8px',
          fontSize: '13px',
          fontWeight: '500',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
        }}
      >
        {usageData && (
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
            <span style={{ color: '#9ca3af' }}>Total Events:</span>
            <span style={{ fontWeight: '600' }}>
              {usageData.value.toLocaleString('en-US')}
            </span>
          </div>
        )}
        {costsData && (
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
            <span style={{ color: '#9ca3af' }}>Total Cost:</span>
            <span style={{ fontWeight: '600' }}>
              ${costsData.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        )}
      </div>
    );
  }
  return null;
};

// Custom legend component (shown above the chart)
const CustomLegend = ({ payload }: any) => {
  return (
    <div style={{ 
      display: 'flex', 
      gap: '24px', 
      justifyContent: 'flex-start',
      fontSize: '13px',
      color: '#9ca3af',
      paddingLeft: '10px'
    }}>
      {payload.map((entry: any, index: number) => (
        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ 
            width: '8px', 
            height: '8px', 
            borderRadius: '50%', 
            backgroundColor: entry.color 
          }} />
          <span style={{ textTransform: 'capitalize' }}>Total {entry.value}</span>
        </div>
      ))}
    </div>
  );
};

const AreaChartComponent = () => {
  return (
    <div style={{ width: '100%', height: '100%' }}>
      {/* Legend above the chart */}
      <div style={{ marginBottom: '16px' }}>
        <CustomLegend payload={[
          { value: 'Usage', color: '#10b981' },
          { value: 'Costs', color: '#8b5cf6' }
        ]} />
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart
          data={data}
          margin={{
            top: 0,
            right: 20,
            left: 20,
            bottom: 0,
          }}
        >
          <defs>
            <linearGradient id="colorUsage" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="colorCosts" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid 
            strokeDasharray="3 3" 
            horizontal={true}
            vertical={true}
            stroke="#e5e7eb"
          />
          <XAxis 
            dataKey="month" 
            tick={{ fill: '#9ca3af', fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            dy={10}
            interval={0}
          />
          {/* Left Y-axis for Costs */}
          <YAxis 
            yAxisId="left"
            tick={{ fill: '#9ca3af', fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            domain={[0, 'auto']}
            hide={true}
          />
          {/* Right Y-axis for Usage */}
          <YAxis 
            yAxisId="right"
            orientation="right"
            tick={{ fill: '#9ca3af', fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            domain={[0, 'auto']}
            hide={true}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#6b7280', strokeWidth: 1, strokeDasharray: '5 5' }} />
          <Area
            yAxisId="right"
            type="monotone"
            dataKey="usage"
            stroke="#10b981"
            strokeWidth={2.5}
            fill="url(#colorUsage)"
            activeDot={{ r: 6, strokeWidth: 0 }}
          />
          <Area
            yAxisId="left"
            type="monotone"
            dataKey="costs"
            stroke="#8b5cf6"
            strokeWidth={2.5}
            fill="url(#colorCosts)"
            activeDot={{ r: 6, strokeWidth: 0 }}
          />
        </AreaChart>
      </ResponsiveContainer>

      {/* Summary stats below the chart */}
      <div style={{ 
        display: 'flex', 
        gap: '35px', 
        marginTop: '32px',
        paddingLeft: '10px'
      }}>
        <div>
          <div style={{ 
            fontSize: '13px', 
            color: '#9ca3af', 
            marginBottom: '3px' 
          }}>
            Total Events
          </div>
          <div style={{ 
            fontSize: '25px', 
            fontWeight: '300',
            color: '#111827',
            display: 'flex',
            alignItems: 'baseline',
            gap: '4px'
          }}>
            {stats.totalUsage.toLocaleString()}
          </div>
          <div style={{ 
            fontSize: '13px', 
            marginTop: '3px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <span style={{ 
              color: stats.usageChange >= 0 ? '#10b981' : '#ef4444',
              fontWeight: '600'
            }}>
              {stats.usageChange >= 0 ? '+' : ''}{stats.usageChange.toFixed(1)}%
            </span>
            <span style={{ color: '#9ca3af' }}>
              compare to last week
            </span>
          </div>
        </div>

        <div>
          <div style={{ 
            fontSize: '13px', 
            color: '#9ca3af', 
            marginBottom: '3px' 
          }}>
            Average Costs
          </div>
          <div style={{ 
            fontSize: '25px', 
            fontWeight: '300',
            color: '#111827',
            display: 'flex',
            alignItems: 'baseline',
            gap: '4px'
          }}>
            ${Math.floor(stats.avgCosts).toLocaleString()}
            <span style={{ fontSize: '24px', color: '#9ca3af' }}>
              .{Math.floor((stats.avgCosts % 1) * 100).toString().padStart(2, '0')}
            </span>
          </div>
          <div style={{ 
            fontSize: '13px', 
            marginTop: '3px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <span style={{ 
              color: stats.costsChange >= 0 ? '#ef4444' : '#10b981',
              fontWeight: '600'
            }}>
              {stats.costsChange >= 0 ? '+' : ''}{stats.costsChange.toFixed(1)}%
            </span>
            <span style={{ color: '#9ca3af' }}>
              compare to last week
            </span>
          </div>
        </div>

        {/* Info text */}
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          paddingLeft: '24px'
        }}>
          <InfoIcon size={20} color="#6b7280" />
          <p style={{
            fontSize: '13px',
            lineHeight: '1.6',
            color: '#6b7280',
            margin: 0
          }}>
            Costs are expected to scale with usage. Deviations—such as faster cost growth, gradual upward drift, or sudden spikes—may point to inefficiencies, model changes, or higher-cost modalities and should be reviewed. Monitoring this relationship helps catch issues early, control spend, and ensure costs align with actual product value.
          </p>
        </div>
      </div>
    </div>
  );
};

export { AreaChartComponent as AreaChart };